import { memo, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import "./copy-clipboard-button.scss";
import PropTypes from "prop-types";
import { CheckmarkCircleStrokeIcon, CopyContentIcon } from "app/icons/navigation";
import { ICON_SIZES } from "app/constants";

const INVISIBLE_STYLE = {
	background: "transparent",
	border: "none",
	outline: "none",
	boxShadow: "none",
	padding: 0,
	opacity: 0,
	height: "1px",
	width: "1px",
};

const CopyToClipboardButtonComponent = ({ text, label, onCopySuccess, onReset }) => {
	const [enable, setEnable] = useState(false);
	const [copied, setCopied] = useState(false);
	const copyTextAreaRef = useRef(null);
	const resetTimeoutRef = useRef(null);

	useEffect(() => {
		if (document.queryCommandSupported("copy")) {
			setEnable(true);
		}

		return () => clearTimeout(resetTimeoutRef.current);
	}, []);

	const copyToClipboard = () => {
		try {
			// @see https://developers.google.com/web/updates/2015/04/cut-and-copy-commands
			// @see http://www.javascriptkit.com/javatutors/copytoclipboard.shtml
			const copyTextarea = copyTextAreaRef.current;
			const range = document.createRange();
			range.selectNodeContents(copyTextarea);
			const selection = window.getSelection();
			selection.removeAllRanges();
			selection.addRange(range);

			const copyResult = document.execCommand("copy");

			setCopied(copyResult);
			resetTimeoutRef.current = setTimeout(() => {
				setCopied(false);
				if (typeof onReset === "function") {
					onReset();
				}
			}, 2000);

			if (typeof onCopySuccess === "function" && copyResult) {
				onCopySuccess(text);
			}
		} catch (err) {
			setCopied(false);
		}
	};

	if (!enable) {
		return false;
	}

	const buttonLabel = copied ? (
		<div className="copy-clipboard-button__label" style={{ display: "block" }}>
			<FormattedMessage id="share.clipboard.button.success.label" />
		</div>
	) : (
		<div className="copy-clipboard-button__label">{label}</div>
	);
	const icon = copied ? (
		<CheckmarkCircleStrokeIcon size={ICON_SIZES.MD} />
	) : (
		<CopyContentIcon size={ICON_SIZES.MD} />
	);

	return (
		<div
			className="copy-clipboard-button"
			data-testid="copy-clipboard-button"
			onClick={copyToClipboard}
		>
			<div ref={copyTextAreaRef} style={INVISIBLE_STYLE}>
				{text}
			</div>
			<span className="copy-clipboard-button__icon">{icon}</span>
			{buttonLabel}
		</div>
	);
};

CopyToClipboardButtonComponent.propTypes = {
	onCopySuccess: PropTypes.func,
	onReset: PropTypes.func,
	text: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export const CopyToClipboardButton = memo(CopyToClipboardButtonComponent);

export default CopyToClipboardButton;
